import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { LineChart } from "../charts/LineChart";
import ColorLegend from "../charts/lib/ColorLegend";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_access_fig_PREPRIM";
import { datasets } from "../data/data_access_fig_PREPRIM_datasets";
import { countryIdIso, foldGeoEntity, getCountryName, mkGemEntityDecoder, useCheckboxState, useCountryEntities, useFigureControlItems, useFigureState, useMultiSelectState, useNamedEntities } from "../domain";
import { useMedia, useOrderedColorScale } from "../hooks";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { io, O, pipe } from "../prelude";
export * from "../data/data_access_fig_PREPRIM";
const DEFAULT_COUNTRIES = [
    "DOM",
    "SEN"
];
export const Data = mkGemEntityDecoder([
    "ind_id",
    "year"
], {
    gender: io.string
});
export const Chart = ({ data })=>{
    const intl = useIntl();
    const env = useConfig();
    const screenMDown = !useMedia(breakpoints.m);
    const [, actions] = useFigureState();
    const [selectedCountries] = useMultiSelectState("countries");
    const [showComparison] = useCheckboxState("toggleComparison");
    const entityData = useNamedEntities(data);
    const countryData = useCountryEntities(entityData);
    const countryControlItems = useFigureControlItems(countryData, (x)=>countryIdIso.unwrap(x.id), (x)=>x.entity_name);
    React.useEffect(()=>{
        actions.setSelectionControl("countries", {
            type: "MultiSelect",
            label: intl.formatMessage(defineMessage({
                id: "Countries",
                defaultMessage: "Countries"
            })),
            selected: O.some(DEFAULT_COUNTRIES)
        }, countryControlItems);
        actions.setToggleControl("toggleComparison", {
            type: "Checkbox",
            label: intl.formatMessage(defineMessage({
                id: "Show income groups",
                defaultMessage: "Show income groups"
            })),
            selected: false
        });
    }, [
        intl,
        actions,
        env.countries,
        countryControlItems
    ]);
    const chartData = React.useMemo(()=>{
        const hasCountry = (country)=>pipe(selectedCountries, O.fold(()=>false, (xs)=>xs.includes(countryIdIso.unwrap(country))));
        return entityData.filter(foldGeoEntity({
            country: ({ id })=>hasCountry(id),
            region: ({ entity_type })=>showComparison ? true : entity_type === "world"
        }));
    }, [
        entityData,
        selectedCountries,
        showComparison
    ]);
    const selectedCountryLabels = React.useMemo(()=>{
        return pipe(selectedCountries, O.map((xs)=>xs.map((x)=>getCountryName(env.countries, countryIdIso.wrap(x)))));
    }, [
        env.countries,
        selectedCountries
    ]);
    const { colorScale, colorLegendValues } = useOrderedColorScale(selectedCountryLabels);
    return <>
      <LineChart tLabel={(s)=>s} height={screenMDown ? M.chartHeight.s : M.chartHeight.m} x="year" timeParse="%Y" timeFormat="%Y" numberFormat=".0%" yTicks={[
        0,
        0.2,
        0.4,
        0.6,
        0.8,
        1
    ]} category="datum.entity_name" colorLegend={false} colorScale={colorScale} values={chartData} markStyle={(x)=>x.entity_type === "world" ? "reference" : x.entity_type === "country" ? "normal" : "muted"} connectLines showTooltip/>
      <ColorLegend inline maxWidth={"100%"} values={colorLegendValues}/>
    </>;
};
export default withFigureIO({
    url: new URL("../data/data_access_fig_PREPRIM.json", import.meta.url).href,
    csv: new URL("../data/data_access_fig_PREPRIM.zip", import.meta.url).href,
    xlsx: new URL("../data/data_access_fig_PREPRIM.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "main",
    datasets
});
